/**
 * Org-scoped localStorage memory for the cost tool. A pharmacist or broker runs
 * the tool many times a day, usually in the same state with the same handful of
 * plans and drugs, so it remembers the partner's working context, never a
 * patient's; the calculator itself stays anonymous.
 *
 * One key per org (`storageKey`). The payload is versioned (`v: 2`), and a
 * stored `v: 1` payload loads with an empty `history`. Parsing is defensive:
 * corrupt, missing or foreign-shaped JSON falls back to defaults instead of
 * crashing. Writes are immediate, because this is a single-tab tool with no
 * storage-event sync. Every storage access is try/caught because localStorage
 * can throw in private-mode Safari; the session still works then but forgets
 * between visits.
 *
 * `history` holds the last few completed checks (inputs and headline numbers),
 * so a returning member's question ("what was that Eliquis number on the
 * Humana plan?") is one tap, not a re-search. For privacy it stays client-side
 * and unnamed, carries no patient identity, expires after `HISTORY_TTL_MS` and
 * clears in one tap: a memory of this terminal's recent inputs, never a patient
 * record.
 */

import { useCallback, useState } from "react";
import { US_STATES, type CostToolPlanOption, type Drug } from "@dearbill/shared";

/** Most-recent-first caps for the quick-pick plan chips and the frequently used drugs. */
const MAX_RECENT_PLANS = 3;
const MAX_RECENT_DRUGS = 6;
/** Most-recent-first history cap: a shelf of today's checks, not an archive. */
const MAX_HISTORY = 8;
/**
 * Entries older than this are dropped on read, so shared terminals don't
 * accumulate months of drug lists.
 */
const HISTORY_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** A remembered plan, with the plan year it was picked under. */
export interface RecentPlan extends CostToolPlanOption {
  /** Set only for a pick under next year; a row without it reads as the current year. */
  planYear?: number;
}

/**
 * One completed check: the inputs (the plan and the picked catalog drugs, enough
 * to restore the rows) and the headline numbers as a preview. A restore always
 * re-runs the estimate against current data rather than showing stored money
 * as current. Nothing patient-shaped is stored: no name, no DOB, no id.
 */
export interface CostToolHistoryEntry {
  id: string;
  /** Epoch ms when the check settled. */
  at: number;
  plan: CostToolPlanOption;
  /** Set only for a check under next year; an entry without it reads as the current year. */
  planYear?: number;
  drugs: Drug[];
  biggestMonth: number | null;
  /**
   * The deductible-met low end of `biggestMonth`. A stored entry may lack it
   * and then reads the single figure.
   */
  biggestMonthMin?: number | null;
  monthlyWithM3p: number | null;
  /**
   * The deductible-met low end of `monthlyWithM3p`. A stored entry may lack it
   * and then reads the single figure.
   */
  monthlyWithM3pMin?: number | null;
  verdict: "helps" | "no_help" | "unknown";
}

export interface CostToolMemory {
  v: 2;
  /** Last chosen US state code; "" = none remembered. */
  state: string;
  /** Most-recent-first, capped, deduped by `id`; a row names its year only for a next-year pick. */
  recentPlans: RecentPlan[];
  /** Most-recent-first, capped, deduped by `rxcui`. */
  recentDrugs: Drug[];
  /**
   * Most-recent-first, capped, deduped by plan id, plan year and rxcui set,
   * and expiring after `HISTORY_TTL_MS`.
   */
  history: CostToolHistoryEntry[];
  /**
   * True once this terminal has dismissed the first-run "How this works"
   * walkthrough. It is optional and reads as false when missing, so a `v: 2`
   * payload without it stays valid.
   */
  seenIntro?: boolean;
}

export interface UseCostToolMemoryReturn {
  memory: CostToolMemory;
  rememberState: (code: string) => void;
  /**
   * Remember a plan. `planYear` is next year for a pick under it, else
   * undefined (the current year); a re-pick replaces the row's year.
   */
  rememberPlan: (plan: RecentPlan, planYear: number | undefined) => void;
  rememberDrug: (drug: Drug) => void;
  /**
   * Record a settled check. A check with the same plan, plan year and rxcui
   * set replaces its old entry, moving it to the front with fresh numbers, so
   * incremental re-checks update one card instead of filling the shelf.
   */
  rememberCheck: (check: Omit<CostToolHistoryEntry, "id" | "at">) => void;
  /** Remove one history entry (the card's X). */
  removeCheck: (id: string) => void;
  /** Empties the history only; the recents and state stay. */
  clearHistory: () => void;
  /** Empties both recents lists, for shared pharmacy terminals. Keeps the state. */
  clearRecents: () => void;
  /** Mark the first-run walkthrough as seen (idempotent). */
  markIntroSeen: () => void;
}

function storageKey(orgId: string): string {
  return `dearbill:partner:cost-tool:${orgId}`;
}

function defaults(): CostToolMemory {
  return { v: 2, state: "", recentPlans: [], recentDrugs: [], history: [], seenIntro: false };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** The render-critical string fields are present, which is enough to trust the row. */
function hasStringFields(value: unknown, fields: readonly string[]): boolean {
  return isRecord(value) && fields.every((f) => typeof value[f] === "string");
}

const PLAN_FIELDS = ["id", "planName", "organizationName", "cmsContractNumber", "cmsPlanId"];
const DRUG_FIELDS = ["rxcui", "drugName", "label"];

function isPlan(value: unknown): value is CostToolPlanOption {
  return hasStringFields(value, PLAN_FIELDS);
}

function isOptionalYear(value: unknown): value is number | undefined {
  return value === undefined || typeof value === "number";
}

function isRecentPlan(value: unknown): value is RecentPlan {
  return isRecord(value) && isOptionalYear(value.planYear) && isPlan(value);
}

function isDrug(value: unknown): value is Drug {
  return hasStringFields(value, DRUG_FIELDS);
}

function isNumberOrNull(value: unknown): value is number | null {
  return value === null || typeof value === "number";
}

/** Full per-entry shape check; a foreign or truncated entry is dropped, not rendered. */
function isHistoryEntry(value: unknown): value is CostToolHistoryEntry {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    typeof value.at === "number" &&
    isPlan(value.plan) &&
    isOptionalYear(value.planYear) &&
    Array.isArray(value.drugs) &&
    value.drugs.length > 0 &&
    value.drugs.every(isDrug) &&
    isNumberOrNull(value.biggestMonth) &&
    (value.biggestMonthMin === undefined || isNumberOrNull(value.biggestMonthMin)) &&
    isNumberOrNull(value.monthlyWithM3p) &&
    (value.monthlyWithM3pMin === undefined || isNumberOrNull(value.monthlyWithM3pMin)) &&
    (value.verdict === "helps" || value.verdict === "no_help" || value.verdict === "unknown")
  );
}

/** The dedupe identity of a check: the plan id, the plan year and the order-insensitive rxcui set. */
function checkKey(entry: Pick<CostToolHistoryEntry, "plan" | "planYear" | "drugs">): string {
  return `${entry.plan.id}|${entry.planYear ?? ""}|${entry.drugs
    .map((d) => d.rxcui)
    .sort()
    .join(",")}`;
}

function load(orgId: string): CostToolMemory {
  try {
    const raw = localStorage.getItem(storageKey(orgId));
    if (raw === null) return defaults();
    const parsed: unknown = JSON.parse(raw);
    // A `v: 1` payload has the same fields without `history`, which starts
    // empty. Anything else that isn't this shape falls back to defaults.
    if (!isRecord(parsed) || (parsed.v !== 1 && parsed.v !== 2)) return defaults();
    // A remembered state must still be a real state code; anything else
    // would silently unlock the plan search against a bogus filter.
    const state =
      typeof parsed.state === "string" && US_STATES.some((s) => s.code === parsed.state)
        ? parsed.state
        : "";
    // Per-row shape checks cover only what the chips render; the type guards
    // trust the rest, because a row this hook wrote carries the full shape.
    const recentPlans = (Array.isArray(parsed.recentPlans) ? parsed.recentPlans : [])
      .filter(isRecentPlan)
      .slice(0, MAX_RECENT_PLANS);
    const recentDrugs = (Array.isArray(parsed.recentDrugs) ? parsed.recentDrugs : [])
      .filter(isDrug)
      .slice(0, MAX_RECENT_DRUGS);
    // Expiry applies on read, so stale checks vanish on the next visit even if
    // nothing writes again.
    const cutoff = Date.now() - HISTORY_TTL_MS;
    const history = (Array.isArray(parsed.history) ? parsed.history : [])
      .filter(isHistoryEntry)
      .filter((e) => e.at >= cutoff)
      .slice(0, MAX_HISTORY);
    // A missing or foreign-typed value reads as false.
    const seenIntro = parsed.seenIntro === true;
    return { v: 2, state, recentPlans, recentDrugs, history, seenIntro };
  } catch {
    return defaults();
  }
}

function save(orgId: string, memory: CostToolMemory): void {
  try {
    localStorage.setItem(storageKey(orgId), JSON.stringify(memory));
  } catch {
    // Storage is unavailable (private mode or quota); keep the in-memory copy.
  }
}

/** UUID for a history entry; Date.now-based fallback for older WebViews. */
function newEntryId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * `orgId` is stable for the life of the mount (it comes from the org shell
 * route), so the memory is loaded once via lazy state init.
 */
export function useCostToolMemory(orgId: string): UseCostToolMemoryReturn {
  const [memory, setMemory] = useState<CostToolMemory>(() => load(orgId));

  const update = useCallback(
    (mutate: (prev: CostToolMemory) => CostToolMemory): void => {
      setMemory((prev) => {
        const next = mutate(prev);
        save(orgId, next);
        return next;
      });
    },
    [orgId],
  );

  const rememberState = useCallback(
    (code: string): void => update((m) => ({ ...m, state: code })),
    [update],
  );

  const rememberPlan = useCallback(
    (plan: RecentPlan, planYear: number | undefined): void =>
      update((m) => {
        // A chip's own stored year never leaks into the new row.
        const { planYear: _stored, ...base } = plan;
        const row = planYear === undefined ? base : { ...base, planYear };
        return {
          ...m,
          recentPlans: [row, ...m.recentPlans.filter((p) => p.id !== plan.id)].slice(
            0,
            MAX_RECENT_PLANS,
          ),
        };
      }),
    [update],
  );

  const rememberDrug = useCallback(
    (drug: Drug): void =>
      update((m) => ({
        ...m,
        recentDrugs: [drug, ...m.recentDrugs.filter((d) => d.rxcui !== drug.rxcui)].slice(
          0,
          MAX_RECENT_DRUGS,
        ),
      })),
    [update],
  );

  const rememberCheck = useCallback(
    (check: Omit<CostToolHistoryEntry, "id" | "at">): void =>
      update((m) => {
        const key = checkKey(check);
        const entry: CostToolHistoryEntry = { ...check, id: newEntryId(), at: Date.now() };
        return {
          ...m,
          history: [entry, ...m.history.filter((e) => checkKey(e) !== key)].slice(0, MAX_HISTORY),
        };
      }),
    [update],
  );

  const removeCheck = useCallback(
    (id: string): void => update((m) => ({ ...m, history: m.history.filter((e) => e.id !== id) })),
    [update],
  );

  const clearHistory = useCallback((): void => update((m) => ({ ...m, history: [] })), [update]);

  const clearRecents = useCallback(
    (): void => update((m) => ({ ...m, recentPlans: [], recentDrugs: [] })),
    [update],
  );

  const markIntroSeen = useCallback(
    (): void => update((m) => (m.seenIntro === true ? m : { ...m, seenIntro: true })),
    [update],
  );

  return {
    memory,
    rememberState,
    rememberPlan,
    rememberDrug,
    rememberCheck,
    removeCheck,
    clearHistory,
    clearRecents,
    markIntroSeen,
  };
}
